import { isNull } from '../../../common/src/utils/common';
import {
  PAYMENT_FLOW,
  PAYMODE,
  EMI_SUBVENTION_STRATEGY,
  ELEMENT_PAYMODE
} from '../../../common/src/constant';
import CONSTANT, { CACHE, UPI_INTENT_MWEB } from './constants';
import ERRORS from './error-codes';
import { get } from '../common/merchant-config';
import { EVENT, log } from '../common/logger';

const _isNullOrUndefined = val => val === undefined || val === null;
const _isInt = n => n % 1 === 0;

const typeMap = {
  DEBIT_CARD: 'card',
  CREDIT_CARD: 'card',
  NET_BANKING: 'nb',
  PPBL: 'ppbl',
  EMI: 'emi',
  UPI: 'upi',
  COD: 'cod',
  PAYTM_DIGITAL_CREDIT: 'pdc',
  SAVED_CARDS: 'savedCards',
  SAVED_VPAS: 'savedVPAs',
  ADVANCE_DEPOSIT_ACCOUNT: 'ada'
};

/**
 * Compares two inputs for equality
 *
 * @param {*} x - original object to be compared
 * @param {*} y - new object to compare with
 * @returns {Boolean} - returns true if both inputs are same else return false
 */
export const compareObjects = (x, y) => {
  if (x === y) return true;
  if (!(x instanceof Object) || !(y instanceof Object)) return false;
  if (
    (typeof x === 'function' && typeof y === 'function') ||
    (x instanceof Date && y instanceof Date) ||
    (x instanceof RegExp && y instanceof RegExp) ||
    (x instanceof String && y instanceof String) ||
    (x instanceof Number && y instanceof Number)
  ) {
    return x.toString() === y.toString();
  }
  for (const p in x) {
    /* eslint no-continue: "off" */
    if (!Object.prototype.hasOwnProperty.call(x, p)) continue;
    if (!Object.prototype.hasOwnProperty.call(y, p)) return false;
    if (x[p] === y[p]) continue;
    if (typeof x[p] !== 'object') return false;
    if (!Object.equals(x[p], y[p])) return false;
  }
  for (const p in y) {
    if (Object.prototype.hasOwnProperty.call(y, p) && !Object.prototype.hasOwnProperty.call(x, p))
      return false;
  }
  return true;
};

const indexOfObject = (myArray, key, value) => {
  for (let i = 0; i < myArray.length; i += 1) {
    if (myArray[i][key] === value) {
      return { obj: myArray[i], ind: i };
    }
  }
  return -1;
};

/**
 * Get property value from nested object
 *
 * @param {Object} obj - object to perform property lookup
 * @param {String} path - the path at which lookup is performed
 * @param {*} defaultValue (optional) -  default value to be reurned if property not found
 * @returns {*} - returns the property value if found else returns dafaultValue
 */
export const getKey = (obj, path, defaultValue) => {
  const result = String.prototype.split
    .call(path, /[,[\].]+?/)
    .filter(Boolean)
    .reduce((res, key) => (!isNull(res) ? res[key] : res), obj);
  return isNull(result) || result === obj ? defaultValue : result;
};

export const isInsufficientBalance = (paymentOptions, cache) => {
  if (paymentOptions) {
    let txnAmount = cache.getData(CONSTANT.TXN_AMOUNT);

    if (getKey(paymentOptions, 'subscriptionDetail.isEnabled', false) && txnAmount === '0') {
      txnAmount = CONSTANT.SUBS_PAYMODE_MIN_AMOUNT;
    }
    if (paymentOptions.wallet) {
      if (parseFloat(paymentOptions.wallet.walletBalance) >= parseFloat(txnAmount)) {
        return false;
      }
    }
  }
  return true;
};

export const applyMerchantPref = (paymodes, mConfig) => {
  const order = getKey(mConfig, 'payMode.order');
  const checkExclude = getKey(mConfig, 'payMode.filter.exclude', []);
  let finalPaymodes = [];
  const labels = getKey(mConfig, 'payMode.labels');
  finalPaymodes = paymodes.filter(item => {
    if (labels && labels[item.type.toUpperCase()]) {
      item.displayName = labels[item.type.toUpperCase()];
    }
    return !(checkExclude.length > 0 && checkExclude.indexOf(item.type.toUpperCase()) > -1);
  });
  let mappedArray = finalPaymodes;
  if (order) {
    mappedArray = order
      .map(item => {
        const indObj = indexOfObject(finalPaymodes, 'type', item.toLowerCase());
        if (indObj !== -1) {
          const removed = finalPaymodes.splice(indObj.ind, 1);
          return removed.length ? removed[0] : -1;
        }
        return -1;
      })
      .filter(item => item !== -1);
    mappedArray = mappedArray.concat(finalPaymodes);
  }
  return mappedArray;
};

export const isAddNPay = (payoptions, cache) => {
  if (
    payoptions &&
    payoptions.wallet &&
    payoptions.wallet.isUsed &&
    isInsufficientBalance(payoptions, cache) &&
    payoptions.paymentFlow === PAYMENT_FLOW.ADDANDPAY
  ) {
    return true;
  }
  return false;
};

export const isHybridOrAddNPay = (paymentOptions, cache) => {
  if (
    paymentOptions &&
    Object.prototype.hasOwnProperty.call(paymentOptions, 'paymentFlow') &&
    paymentOptions.paymentFlow !== PAYMENT_FLOW.NONE &&
    getKey(paymentOptions, 'wallet.isUsed', false) &&
    isInsufficientBalance(paymentOptions, cache)
  ) {
    // Add n Pay and Hybrid Flow
    return true;
  }
  // pg only
  return false;
};
export const isHybridOrAddNPayWithSufficientWalletBalance = (paymentOptions, cache) => {
  if (
    paymentOptions &&
    Object.prototype.hasOwnProperty.call(paymentOptions, 'paymentFlow') &&
    paymentOptions.paymentFlow !== PAYMENT_FLOW.NONE &&
    getKey(paymentOptions, 'wallet.isUsed', false) &&
    !isInsufficientBalance(paymentOptions, cache)
  ) {
    // Add n Pay and Hybrid Flow
    return true;
  }
  // pg only
  return false;
};

export const isMgvSufficient = (payoption, cache) => {
  if (payoption) {
    const taxAmount = cache.getData(CONSTANT.TXN_AMOUNT);
    if (parseFloat(payoption.availableBalance) >= parseFloat(taxAmount)) {
      return true;
    }
  }
  return false;
};

/**
 * Compare two strings case insensitively
 *
 * @export
 * @param {string} first
 * @param {string} second
 * @returns {boolean} are string equal?
 */
export function equalsIgnoreCase(first, second) {
  return (first || '').toLowerCase() === (second || '').toLowerCase();
}

/**
 * Validate whether an input is an Object or not.
 *
 * @export
 * @param {*} input
 * @returns {boolean} is input is an Object?
 */
export function isObject(input) {
  return Object.prototype.toString.call(input) === '[object Object]';
}

/**
 * Validate whether an input is an Array or not.
 *
 * @export
 * @param {*} input
 * @returns {boolean} is input is an Array?
 */
export function isArray(input) {
  return Array.isArray(input);
}

export const money = amount => {
  if (!Number.isNaN(amount)) {
    return parseFloat(Math.round(amount * 100) / 100)
      .toFixed(2)
      .replace('.00', '');
  }
  return amount;
};

const processWallet = balanceObj => {
  if (!balanceObj) return {};
  const wallet = {};
  if (balanceObj.payChannelOptions && balanceObj.payChannelOptions.length) {
    const paymode = balanceObj.payChannelOptions[0];
    if (
      paymode.balanceInfo &&
      paymode.balanceInfo.accountBalance &&
      paymode.balanceInfo.accountBalance.value
    ) {
      wallet.walletBalance = money(
        balanceObj.payChannelOptions[0].balanceInfo.accountBalance.value
      );
    }
    if (paymode.balanceInfo && paymode.balanceInfo.subWalletDetails) {
      wallet.subWalletDetails = paymode.balanceInfo.subWalletDetails;
    }
    wallet.isUsed = true;
    wallet.isEnabled = true;
    wallet.isDisplay = true;
    /* adding paymentmode check in wallet object so as to identify wallet object easily */
    wallet.paymentMode = balanceObj.paymentMode;
    if (paymode.isDisabled) {
      wallet.isDisplay = paymode.isDisabled.status !== 'true';
      wallet.disableMsg = paymode.isDisabled.msg;
      wallet.isEnabled =
        paymode.isDisabled.status === 'false' && paymode.isDisabled.merchantAccept === 'true';
    }
    wallet.displayName = balanceObj.displayName;

    if (!wallet.walletBalance || money(wallet.walletBalance) <= 0) {
      wallet.isUsed = false;
    }
    wallet.feeAmount = balanceObj.feeAmount ? balanceObj.feeAmount : 0;
    wallet.taxAmount = balanceObj.taxAmount ? balanceObj.taxAmount : 0;
  }
  return wallet;
};

const processMgv = mgvObj => {
  if (!mgvObj) return null;
  const mgv = {};
  if (mgvObj.payChannelOptions && mgvObj.payChannelOptions.length) {
    const paymode = mgvObj.payChannelOptions[0];
    if (
      paymode.balanceInfo &&
      paymode.balanceInfo.accountBalance &&
      paymode.balanceInfo.accountBalance.value
    ) {
      mgv.availableBalance = money(mgvObj.payChannelOptions[0].balanceInfo.accountBalance.value);
    }
    paymode.templateId && (mgv.templateId = paymode.templateId);
    mgv.isEnabled = true;
    mgv.isHybridDisabled = paymode.isHybridDisabled;
    mgv.id = mgvObj.id;
    mgv.paymentMode = mgvObj.paymentMode;
    mgv.type = mgvObj.type;
    if (paymode.isDisabled) {
      mgv.disableMsg = paymode.isDisabled.msg;
      mgv.isEnabled =
        paymode.isDisabled.status === 'false' && paymode.isDisabled.merchantAccept === 'true';
    }
    mgv.displayName = mgvObj.displayName;

    if (!mgv.availableBalance || money(mgv.availableBalance) <= 0) {
      mgv.isEnabled = false;
    }
  }
  return mgv;
};

const fetchSavedVpa = profileDetails => {
  const VPA_AUTH_MODE = 'USRPWD';
  const savedVpaList = [];
  const { vpaDetails, bankAccounts } = profileDetails;
  let primaryVpaDetails;
  vpaDetails.forEach(vpa => {
    /* pick the vpa detail which is set primary */
    if (vpa.primary) {
      primaryVpaDetails = vpa;
    }
  });
  if (bankAccounts && bankAccounts.length) {
    bankAccounts.forEach(bankAccount => {
      const sVPAInstrument = {};
      /* in fpo v2 vpa detail comes seperately and hence have to pick name from vpa Details */
      sVPAInstrument.vpaId = getKey(primaryVpaDetails, 'name', getKey(bankAccount, 'accRefId'));
      sVPAInstrument.bankName = bankAccount.bank;
      /* slice -4 is being used to show the last 4 digits of the account number
       */
      sVPAInstrument.bankAccountNo = bankAccount.maskedAccountNumber.toString().slice(-4);
      /* todo this needs to be checked for what payeedata has to be sent for upipush txn
       *  currently setting bankaccount as whole vpaDetail was set in fpo v1 */
      sVPAInstrument.payeeData = bankAccount;
      sVPAInstrument.authMode = VPA_AUTH_MODE;
      sVPAInstrument.displayName = `${sVPAInstrument.bankName} - UPI`;
      savedVpaList.push(sVPAInstrument);
    });
  }
  return savedVpaList;
};

const addPaymodeProperties = (list, subscription) => {
  if (!list) return [];
  let cardCount = 0;
  let idx;
  return list.filter((item, index) => {
    item.paymentMode && (item.type = typeMap[item.paymentMode]);
    if (item.type === 'card') {
      cardCount += 1;
      item.displayName = CONSTANT.CARD_AND_DEBIT;
    }
    // handling of display name if only one from credit , debit card is coming as paymode
    if (cardCount === 1 && item.type === 'card' && getKey(subscription, 'isEnabled', false)) {
      item.showDisplayName = true;
      idx = index;
    }
    if (cardCount >= 2 && item.type === 'card' && getKey(subscription, 'isEnabled', false)) {
      list[idx].showDisplayName = false;
    }
    item.isEnabled = !(item.isDisabled && item.isDisabled.status === 'true');
    return !(item.type === 'card' && cardCount >= 2);
  });
};

const isAndroidMweb = () => {
  const userAgent = window.navigator.userAgent || '';
  const isAndroid = /android/i.test(userAgent);
  const isAndroidWebViewKitKat =
    /android/i.test(userAgent) &&
    /version\/[0-9].[0-9]/i.test(userAgent) &&
    /Chrome\/\d*.\d*.\d*.\d*/i.test(userAgent);
  const isAndroidWebViewLollipop = /android/i.test(userAgent) && /wv/i.test(userAgent);
  const isUcBrowser = /ucbrowser|ucweb/i.test(userAgent);
  if (isAndroid && !isAndroidWebViewKitKat && !isAndroidWebViewLollipop && !isUcBrowser) {
    return true;
  }
  return false;
};

// const enableMidForUpiIntentMweb = (mid, orderId) => {
//   const enabledMidsUpiIntent = {
//     wHNUTH68224456779429: true,
//     AliSem26864341878851: true,
//     qNLEbz38834473442956: true,
//     slSpEG68327288525417: true,
//     ankpgp42986102247938: true,
//     iteite32049126720624: true,
//     zMXEFR83237974807152: true,
//     uCqBst06907425123687: true,
//     thStpg34491519591324: true,
//     nJuReA16339161897045: true,
//     ewOkIj89660348606926: true,
//     uSjDDx33134705348761: true,
//     phWccH34827864229470: true,
//     Jltyhs197678951592363: true,
//     BzsmkK78937952374483: true,
//     CppIkS77240431629035: true,
//     DuhxxC28068076146265: true,
//     EmEdCS60185669890238: true,
//     NIRBHA60344112284894: true,
//     TnvdaD49942329337141: true,
//     YLikIO89313606956833: true
//   };
//   // enabledMidsUpiIntent.hasOwnProperty(mid) && enabledMidsUpiIntent[mid] && orderId.indexOf('khata') >= 0
//   return !!(enabledMidsUpiIntent.hasOwnProperty(mid) && enabledMidsUpiIntent[mid]);
// };

const getHidenVisibalityProperties = () => {
  let hidden;
  let visibilityChange;
  if (typeof document.hidden !== 'undefined') {
    // Opera 12.10 and Firefox 18 and later support
    hidden = 'hidden';
    visibilityChange = 'visibilitychange';
  } else if (typeof document.mozHidden !== 'undefined') {
    hidden = 'mozHidden';
    visibilityChange = 'mozvisibilitychange';
  } else if (typeof document.msHidden !== 'undefined') {
    hidden = 'msHidden';
    visibilityChange = 'msvisibilitychange';
  } else if (typeof document.webkitHidden !== 'undefined') {
    hidden = 'webkitHidden';
    visibilityChange = 'webkitvisibilitychange';
  }
  return { hidden, visibilityChange };
};

export const setUpiIntentMwebDisplay = cache => {
  const option = cache.getData(CACHE.PAYMENT_OPTIONS);
  if (isAndroidMweb() && getKey(option, `upiAppsPayModeEnabled`, false) === true) {
    const hiddenVisibilityObj = getHidenVisibalityProperties();
    if (hiddenVisibilityObj && hiddenVisibilityObj.hidden && hiddenVisibilityObj.visibilityChange) {
      cache.setData(UPI_INTENT_MWEB.IS_UPI_INTENT_MWEB_ENABLED, true);
      cache.setData(UPI_INTENT_MWEB.VISIBILITY_HIDDEN_VALUE, hiddenVisibilityObj);
      log({ eventType: EVENT.IS_INTENT_SUPPORTED, value: true });
    }
  } else {
    cache.setData(UPI_INTENT_MWEB.IS_UPI_INTENT_MWEB_ENABLED, false);
    log({ eventType: EVENT.IS_INTENT_SUPPORTED, value: false });
  }
};

const checkForRetryPayMode = (cache, currentPaymode) => {
  const retryData = cache.getData(CACHE.RETRY_DATA);
  try {
    if (retryData && parseInt(retryData.selectedPaymentModeId, 10) === currentPaymode.id) {
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
};

const isValidUpiPayChannel = (channel, cache) => {
  const channelCode = getKey(channel, 'channelCode');
  return (
    equalsIgnoreCase(channelCode, CONSTANT.UPI) ||
    (equalsIgnoreCase(channelCode, CONSTANT.UPIPUSH) &&
      isAndroidMweb() &&
      getHidenVisibalityProperties() &&
      cache.getData(CONSTANT.IS_UPI_APPS_PAYMODE_ENABLED))
  );
};

const processPaymodes = (paymodes, cache, subscription = null) => {
  let id = 0;
  // setting wallet id as -1 initially since index 0 being passed  was giving issues on render of wallet
  let walletId = -1;
  let defaultRetryPaymodeSelection = null; // this var will be used in showing default paymode selected to user in case of retry
  const localStorageSavedPaymode = getKey(cache.getData(CONSTANT.CURRENT_PAYMODE), 'paymodeUsed');
  const uniqCardNo = getKey(cache.getData(CONSTANT.CURRENT_PAYMODE), 'uniqueCardData');
  paymodes.paymentModes = addPaymodeProperties(paymodes.paymentModes, subscription);

  if (paymodes.paymentModes) {
    paymodes.paymentModes.forEach((paymode, index) => {
      if (
        ((paymode.displayName && paymode.displayName === localStorageSavedPaymode) ||
          (paymode.type && paymode.type === 'card' && paymode.type === localStorageSavedPaymode)) &&
        paymode.paymentMode !== CONSTANT.GIFT_VOUCHER
      ) {
        cache.setData(CONSTANT.FOUND_LAST_USED_PAYMODE, true);
        paymodes.lastUsedPaymode = paymode;
      }
      if (paymode.paymentMode === 'BALANCE') {
        walletId = index;
      }
      if (paymode.paymentMode === CONSTANT.GIFT_VOUCHER) {
        id += 1;
        paymode.type = 'mgv';
        paymode.id = id;
        const mgvObject = processMgv(JSON.parse(JSON.stringify(paymode)));
        if (
          paymode.displayName &&
          paymode.displayName === localStorageSavedPaymode &&
          isMgvSufficient(mgvObject, cache)
        ) {
          cache.setData(CONSTANT.FOUND_LAST_USED_PAYMODE, true);
          paymodes.lastUsedPaymode = paymode;
        }
      }
      if (paymode.paymentMode === PAYMODE.LOGIN) {
        id += 1;
        paymode.type = PAYMODE.LOGIN.toLowerCase();
        paymode.id = id;
      }
      if (paymode.paymentMode === PAYMODE.UPI) {
        cache.setData(CONSTANT.IS_UPI_TXN, true);
        paymode.isEnabled = false;
        if (paymode.payChannelOptions) {
          paymode.payChannelOptions.map(channel => {
            // Enable the UPI for Upi Intent also, iff, userAgent is android mweb and hiddenVisibily supported
            if (isValidUpiPayChannel(channel, cache)) {
              paymode.isEnabled = true;
            }
            return channel;
          });
        }
      }

      if (paymode.paymentMode === 'NET_BANKING' || paymode.paymentMode === 'EMI') {
        paymode.data = {};
        if (getKey(paymode, 'payChannelOptions')) {
          paymode.data.banks = [...paymode.payChannelOptions];
        }
        delete paymode.payChannelOptions;
      }
      !paymode.name && (paymode.name = paymode.displayName);
      id += 1;
      paymode.id = id;
      !paymode.type && (paymode.type = paymode.paymentMode);
      if (checkForRetryPayMode(cache, paymode)) {
        defaultRetryPaymodeSelection = paymode;
      }
    });
  }

  if (paymodes.savedInstruments) {
    paymodes.savedInstruments.forEach(instrument => {
      id += 1;
      instrument.id = id;
      instrument.type = 'sc';
      instrument.firstSix = instrument.cardDetails.firstSixDigit;
      instrument.lastFour = instrument.cardDetails.lastFourDigit;
      if (checkForRetryPayMode(cache, instrument)) {
        defaultRetryPaymodeSelection = instrument;
      }
      if (
        instrument.displayName &&
        instrument.displayName === localStorageSavedPaymode &&
        instrument.lastFour === uniqCardNo
      ) {
        cache.setData(CONSTANT.FOUND_LAST_USED_PAYMODE, true);
        paymodes.lastUsedPaymode = instrument;
      }
    });
  }
  if (getKey(paymodes, 'upiProfile.respDetails.profileDetail', null)) {
    const profileDetail = getKey(paymodes, 'upiProfile.respDetails.profileDetail', null);
    paymodes.savedVPas = fetchSavedVpa(profileDetail);
    delete paymodes.upiProfile;
  }

  if (paymodes.savedMandateBanks && paymodes.savedMandateBanks.length) {
    paymodes.savedMandateBanks.forEach(bankMandate => {
      id += 1;
      bankMandate.id = id;
      bankMandate.type = CONSTANT.SAVED_BANK_MANDATE_PAY_TYPE;
      bankMandate.paymentMode = CONSTANT.SBM;
      bankMandate.lastFour = bankMandate.maskedAccountNumber.slice(-4);
    });
  }

  if (paymodes.savedVPas) {
    paymodes.savedVPas.forEach(vpa => {
      id += 1;
      vpa.id = id;
      vpa.paymentMode = CONSTANT.UPI;
      vpa.type = 'upipush';
      if (checkForRetryPayMode(cache, vpa)) {
        defaultRetryPaymodeSelection = vpa;
      }
      if (vpa.displayName && vpa.displayName === localStorageSavedPaymode) {
        cache.setData(CONSTANT.FOUND_LAST_USED_PAYMODE, true);
        paymodes.lastUsedPaymode = vpa;
      }
    });
  }
  if (defaultRetryPaymodeSelection) {
    paymodes.defaultRetryPaymodeSelection = defaultRetryPaymodeSelection;
  }
  //  paymodes.paymentModes = addPaymodeProperties(paymodes.paymentModes);
  return walletId;
};

const _IS_MOBILE_REGEX = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|OperaMini/i;

export const isMobileDevice = () => {
  return _IS_MOBILE_REGEX.test(window.navigator.userAgent);
};

export const parseCheckoutElementPayMode = (
  paymentOptions,
  checkoutElementPayMode,
  loginChangeFlow,
  cache
) => {
  let paymentModes = getKey(paymentOptions, 'merchantPayOption.paymentModes');
  let isPayModeNotFound = false;
  const isPayWithPaytm = equalsIgnoreCase(ELEMENT_PAYMODE.PAY_WITH_PAYTM, checkoutElementPayMode);

  if (
    !isPayWithPaytm ||
    (isPayWithPaytm && !equalsIgnoreCase(paymentOptions.paymentFlow, PAYMENT_FLOW.ADDANDPAY))
  ) {
    paymentOptions.paymentFlow = PAYMENT_FLOW.NONE;
  }

  if (paymentModes && checkoutElementPayMode) {
    let requestedPaymodes = { [checkoutElementPayMode.toUpperCase()]: true };

    if (isPayWithPaytm) {
      requestedPaymodes = {
        [CONSTANT.POSTPAID]: true,
        [CONSTANT.PPBL]: true,
        [CONSTANT.WALLET]: true,
        [CONSTANT.LOGIN]: true,
        [CONSTANT.GIFT_VOUCHER]: true
      };
    }
    paymentModes = paymentModes.filter(
      mode =>
        (requestedPaymodes[(mode.paymentMode || mode.type || '').toUpperCase()] ||
          requestedPaymodes[(typeMap[mode.paymentMode] || '').toUpperCase()]) &&
        (equalsIgnoreCase(getKey(mode, 'paymentMode'), PAYMODE.WALLET) ||
          getKey(mode, 'isDisabled.status') !== 'true')
    );

    // UPI paymode handling
    if (
      equalsIgnoreCase(checkoutElementPayMode, ELEMENT_PAYMODE.UPI) &&
      paymentModes.length === 1
    ) {
      const isUPIAllowed = getKey(paymentModes[0], 'payChannelOptions', []).some(channel =>
        isValidUpiPayChannel(channel, cache)
      );
      if (!isUPIAllowed) {
        paymentModes = [];
      }
    }
    paymentOptions.merchantPayOption.paymentModes = paymentModes;
    isPayModeNotFound = paymentModes.length === 0;
  }

  if (
    // !isPayWithPaytm &&
    getKey(paymentOptions, 'qrDetail')
  ) {
    const isScanAndPay =
      !isMobileDevice() && equalsIgnoreCase(checkoutElementPayMode, ELEMENT_PAYMODE.SCAN_AND_PAY);
    if (
      (isPayWithPaytm && getKey(paymentOptions, 'qrDetail.upiQR')) ||
      (!isPayWithPaytm && !isScanAndPay)
    ) {
      delete paymentOptions.qrDetail;
    }

    if (isScanAndPay) {
      isPayModeNotFound = false;
    }
  }

  const savedInstruments = getKey(paymentOptions, 'merchantPayOption.savedInstruments');
  if (savedInstruments) {
    if (equalsIgnoreCase(PAYMODE.EMI, checkoutElementPayMode)) {
      paymentOptions.merchantPayOption.savedInstruments = savedInstruments.filter(
        si => si.isEmiAvailable
      );
    } else if (
      !(
        equalsIgnoreCase(PAYMODE.CARD, checkoutElementPayMode) ||
        (equalsIgnoreCase(ELEMENT_PAYMODE.PAY_WITH_PAYTM, checkoutElementPayMode) &&
          getKey(paymentOptions, 'loginInfo.userLoggedIn'))
      )
    ) {
      delete paymentOptions.merchantPayOption.savedInstruments;
    }
  }

  if (!equalsIgnoreCase(ELEMENT_PAYMODE.PAY_WITH_PAYTM, checkoutElementPayMode)) {
    if (getKey(paymentOptions, 'merchantPayOption.upiProfile.respDetails.profileDetail')) {
      delete paymentOptions.merchantPayOption.upiProfile.respDetails.profileDetail;
    }

    // if (getKey(paymentOptions, 'merchantPayOption.userProfileSarvatra.response.vpaDetails')) {
    //   delete paymentOptions.merchantPayOption.userProfileSarvatra.response.vpaDetails;
    // }

    // if (getKey(paymentOptions, 'merchantPayOption.userProfileSarvatra.response.bankAccounts')) {
    //   delete paymentOptions.merchantPayOption.userProfileSarvatra.response.bankAccounts;
    // }
  }

  if (isPayModeNotFound && !loginChangeFlow) {
    throw new Error('Paymode not available!');
  }

  return getKey(paymentOptions, 'merchantPayOption');
};

export const parseData = (input, cache, checkoutElementPayMode, loginChangeFlow = false) => {
  if (!input || !isObject(input)) {
    return [];
  }
  cache.setData(
    CONSTANT.IS_UPI_APPS_PAYMODE_ENABLED,
    getKey(input, `upiAppsPayModeEnabled`, false)
  );
  let paymodes = checkoutElementPayMode
    ? parseCheckoutElementPayMode(input, checkoutElementPayMode, loginChangeFlow, cache)
    : input.merchantPayOption;

  if (
    isObject(input.loginInfo) &&
    (!checkoutElementPayMode ||
      equalsIgnoreCase(ELEMENT_PAYMODE.PAY_WITH_PAYTM, checkoutElementPayMode))
  ) {
    input.loginInfo.id = 'loginInfo';
    if (!getKey(input, 'loginInfo.userLoggedIn')) {
      const loginObj = {
        paymentMode: PAYMODE.LOGIN
      };
      paymodes.paymentModes.unshift(loginObj);
    }
  }

  if (getKey(input, 'allowedRetryCountsForMerchant', 0) > 0) {
    cache.setData(
      CONSTANT.ALLOWED_MERCHANT_RETRY_COUNTS,
      0
      // getKey(input, 'allowedRetryCountsForMerchant', 0) + 1
    );
  } else {
    cache.setData(CONSTANT.ALLOWED_MERCHANT_RETRY_COUNTS, 0);
  }

  if (Object.prototype.hasOwnProperty.call(input, 'link') && input.link && input.link.linkType) {
    cache.setData(CONSTANT.IS_LINK_TXN, true);
  } else {
    cache.setData(CONSTANT.IS_LINK_TXN, false);
  }
  const { merchantPayOption, addMoneyPayOption, subscriptionDetail } = input;
  if (merchantPayOption) {
    const walletId = processPaymodes(merchantPayOption, cache, subscriptionDetail);

    if (walletId !== -1) {
      input.wallet = processWallet(merchantPayOption.paymentModes.splice(walletId, 1)[0]);
      input.wallet && (input.wallet.isWalletOnly = input.walletOnly);
      if (input.paymentFlow === PAYMENT_FLOW.ADDANDPAY) {
        input.wallet.isUsed = true;
      }
      if (getKey(subscriptionDetail, 'isEnabled', false) && merchantPayOption.paymentModes.length) {
        input.wallet.isUsed = false;
      }
      merchantPayOption.paymentModes = merchantPayOption.paymentModes.filter(paymode => {
        if (paymode.paymentMode === CONSTANT.GIFT_VOUCHER) {
          input.mgv = processMgv(paymode);
          return false;
        }
        return true;
      });
      if (cache.getData(CONSTANT.TXN_AMOUNT)) {
        if (
          getKey(input, 'wallet.isEnabled') &&
          isInsufficientBalance(input, cache) &&
          !isHybridOrAddNPay(input, cache) &&
          !getKey(input, 'subscriptionDetail.isEnabled', false)
        ) {
          input.wallet.isUsed = false;
          input.wallet.isEnabled = false;
        }
        if (
          getKey(input, 'mgv.isEnabled') &&
          isMgvSufficient(input.mgv, cache) &&
          isInsufficientBalance(input, cache) &&
          (getKey(cache.getData(CONSTANT.CURRENT_PAYMODE), 'paymodeUsed') &&
            getKey(cache.getData(CONSTANT.CURRENT_PAYMODE), 'paymodeUsed').indexOf('GIFT_VOUCHER') >
              -1)
        ) {
          input.wallet && (input.wallet.isUsed = false);
        }
      }
    }

    const usedPaymode = cache.getData(CONSTANT.CURRENT_PAYMODE);
    if (
      usedPaymode &&
      isInsufficientBalance(input, cache) &&
      cache.getData(CONSTANT.FOUND_LAST_USED_PAYMODE)
    ) {
      if (!usedPaymode.isWalletCheck && isHybridOrAddNPay(input, cache)) {
        paymodes = input.merchantPayOption;
        input.wallet.isUsed = false;
      }
    }
  }
  if (addMoneyPayOption) {
    if (getKey(addMoneyPayOption, 'upiProfile.respDetails.profileDetail')) {
      delete addMoneyPayOption.upiProfile.respDetails.profileDetail;
    }
    processPaymodes(addMoneyPayOption, cache);
  }
  // Set add n pay paymodes if ADDANDPAY flag present
  if (isAddNPay(input, cache)) {
    paymodes = input.addMoneyPayOption;
  } else if (merchantPayOption) {
    paymodes = input.merchantPayOption;
  } else {
    paymodes = [];
  }
  input.payData = paymodes;
  return paymodes;
};

const _numberWithCommas = x => {
  /* setting x to toString because length throws error when number was being passed */
  x = x.toString();

  if (_isNullOrUndefined(x)) {
    return '';
  }

  if (x.length === 0) {
    return '';
  }

  if (x.indexOf(',') !== -1) {
    x = x.toString().replace(/,/g, '');
  }
  if (x && !_isInt(x)) {
    x = parseFloat(x).toFixed(2); // Set to 2 decimal value
  }
  // if(x){
  //     x = parseFloat(x).toFixed(2); // Set to 2 decimal value
  // }

  const numbers = x.toString().split('.');
  let output = '';
  // number with commas
  if (numbers.length >= 1) {
    if (window.Number && window.Number.prototype.toLocaleString) {
      output = parseFloat(numbers[0]).toLocaleString('en-in');
    } else {
      output = numbers[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  }
  // If number has floating part
  if (numbers.length === 2) {
    if (!(numbers[1] === '00' || numbers[1] === '0')) {
      output += `.${numbers[1]}`;
    }
  }
  return output;
};

export const commas = _numberWithCommas;
export const isNullOrUndefined = _isNullOrUndefined;

/**
 * Make a method restricted to be called only once and return the cached value
 * @param {Function} cb - the method that needs to be made call only once
 * @param {Object} ctx (optional) - the context to the the provided method needs to be bound to
 * @returns {*} - returns a method that invokes tha passed method
 */

export const callOnce = (cb, ctx) => {
  if (!(cb instanceof Function)) return () => {};
  let called = false;
  let returned;
  return function callOnceFunc(...args) {
    if (!called) {
      called = true;
      returned = ctx ? cb.call(ctx, ...args) : cb(...args);
    }
    return returned;
  };
};

export const getEncodedQs = obj => {
  const arr = [];
  if (!obj) {
    return '';
  }
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      arr.push(`${key}=${window.encodeURIComponent(obj[key])}`);
    }
  }
  return arr.join('&');
};

export const staticPath = () => {
  let _url = STATIC_HOST;

  try {
    if (typeof window !== 'undefined') {
      if (VERSION) {
        _url += `/${APP_NAME}/${VERSION}`;
      }
    }
  } catch (e) {}
  return _url;
};

export const paymentFlow = (paymentOption, cache) => {
  let flow = PAYMENT_FLOW.NONE;
  if (paymentOption && isHybridOrAddNPay(paymentOption, cache)) {
    flow = paymentOption.paymentFlow;
  }
  return flow;
};

export const defaultSelected = (paymentOptions, cache) => {
  let selectedPaymode = null;
  const elementPaymode = cache.getData(CACHE.CHECKOUT_ELEMENT_PAYMODE);
  const isEmiElementPaymode = equalsIgnoreCase(elementPaymode, ELEMENT_PAYMODE.EMI);
  const {
    paymentModes,
    savedVPas,
    savedInstruments,
    savedMandateBanks,
    defaultRetryPaymodeSelection,
    lastUsedPaymode
  } = paymentOptions.payData;
  const isMGV = getKey(cache.getData(CONSTANT.CURRENT_PAYMODE), 'isMGV', undefined);
  // Check to not set local storage paymode when walletBalance is sufficient
  if (lastUsedPaymode && isInsufficientBalance(paymentOptions, cache)) {
    // MGV shouldn't be selected in case it's balance is not sufficient
    if ((isMGV && isMgvSufficient(paymentOptions.mgv, cache)) || !isMGV) {
      cache.setData(CONSTANT.STORAGE_USED, true);
      return lastUsedPaymode;
    }
  }

  if (defaultRetryPaymodeSelection) {
    selectedPaymode = defaultRetryPaymodeSelection;
    delete paymentOptions.payData.defaultRetryPaymodeSelection;
  } else if (savedVPas && savedVPas.length > 0) {
    selectedPaymode = savedVPas[0];
  } else if (!isEmiElementPaymode && savedInstruments && savedInstruments.length > 0) {
    selectedPaymode = savedInstruments[0];
  } else if (!isEmiElementPaymode && savedMandateBanks && savedMandateBanks.length) {
    selectedPaymode = savedMandateBanks[0];
  } else if (paymentModes && paymentModes.length > 0) {
    selectedPaymode = paymentModes[0];
  }
  if (
    (isInsufficientBalance(paymentOptions, cache) && !isMgvSufficient(paymentOptions.mgv, cache)) ||
    (!lastUsedPaymode && isInsufficientBalance(paymentOptions, cache))
  ) {
    return selectedPaymode;
  }

  if (
    getKey(paymentOptions, 'subscriptionDetail.isEnabled', false) &&
    !getKey(paymentOptions, 'wallet.isUsed', true)
  ) {
    return selectedPaymode;
  }
  return null;
};

export const clientMessage = (key, cache) => {
  const config = cache.getData(CACHE.MERCHANT_CONFIG);
  if (config && config.mapClientMessage) {
    return config.mapClientMessage[key] || {};
  }
  return {};
};
export const applyMerchPref = (_paymentOptions, mConfig) => {
  const paymentOptions = _paymentOptions;
  if (getKey(paymentOptions, 'addMoneyPayOption.paymentModes')) {
    paymentOptions.addMoneyPayOption.paymentModes = applyMerchantPref(
      paymentOptions.addMoneyPayOption.paymentModes,
      mConfig
    );
  }
  if (getKey(paymentOptions, 'merchantPayOption.paymentModes')) {
    paymentOptions.merchantPayOption.paymentModes = applyMerchantPref(
      paymentOptions.merchantPayOption.paymentModes,
      mConfig
    );
  }
};

export const loggedInChanged = (payOptions, cache, checkoutElementPayMode) => {
  parseData(payOptions.cashierData, cache, checkoutElementPayMode, true);
  cache.setData(CACHE.PAYMENT_OPTIONS, payOptions.cashierData);
  applyMerchPref(payOptions.cashierData, get());
  return payOptions;
};

export const calculateAmountPayable = (
  txnAmount,
  walletObj,
  commaSeparatedAmount = true,
  subscription = null
) => {
  let walletBalance = 0;
  let amountPayable = txnAmount;
  /**
   * for zero amount subscription setting txn value to 1
   */
  if (getKey(subscription, 'isEnabled', false) && txnAmount === '0') {
    amountPayable = CONSTANT.SUBS_PAYMODE_MIN_AMOUNT;
  }

  if (getKey(walletObj, 'isUsed', false)) {
    walletBalance = walletObj.walletBalance;
    if (parseFloat(walletBalance) < parseFloat(amountPayable)) {
      amountPayable = parseFloat(amountPayable) - parseFloat(walletBalance);
    }
  }
  return commaSeparatedAmount ? _numberWithCommas(amountPayable.toString()) : amountPayable;
};

export const updateSlideIndex = (activeInd, helpTextLen) => activeInd === helpTextLen - 1;

export const touchStart = e => {
  const touchObj = e.changedTouches[0];
  e.preventDefault();
  return {
    startX: touchObj.pageX,
    startY: touchObj.pageY
  };
};

export const touchEnd = (e, startX, threshold) => {
  const touchObj = e.changedTouches[0];
  const swipeDist = touchObj.pageX - startX;
  return swipeDist >= threshold;
};

export const handleSwipeCtrl = (isrightswipe, index, helpText) => {
  const actvInd = isrightswipe
    ? index - 1 === -1
      ? 0
      : index - 1
    : index + 1 === helpText.length
    ? helpText.length - 1
    : index + 1;
  const okBtnVal = actvInd === helpText.length - 1;
  return { actvInd, okBtnVal };
};

export const errorObject = errorCode => ({
  hasError: true,
  code: errorCode,
  message: ERRORS.ERROR_MESSAGE[errorCode] || ''
});

/**
 * Handle save card save maessage based on subscription
 *
 * @export
 * @param {Object} subscription  subscription object
 * @param {Object} paymentOptions payment option object
 * @returns {Object} saveCard configrations
 */
export const handleSaveCardOnSubscription = (subscription, paymentOptions, cache) => {
  const output = {
    disabled: false,
    displayName: clientMessage(PAYMODE.CARD.toLowerCase(), cache).label.savedCard,
    subscriptionEnabled: false
  };
  if (subscription && subscription.isEnabled) {
    const { wallet, paymentFlow } = paymentOptions;
    output.displayName = clientMessage(CONSTANT.SUBSCRIPTION.toLowerCase(), cache).label.savedCard;
    if (
      !(getKey(wallet, 'isUsed') && subscription.nonSpecificPayMode) ||
      (paymentFlow !== PAYMENT_FLOW.ADDANDPAY ||
        (subscription.saveCardMandatoryAddNPay &&
          getKey(wallet, 'isUsed') &&
          paymentFlow === PAYMENT_FLOW.ADDANDPAY))
    ) {
      output.disabled = true;
    }
    output.subscriptionEnabled = true;
  }
  return output;
};
/**
 * Picks first level key-value from an object for request keys
 *
 * @export
 * @param {Object} obj
 * @param {Array} keysToPluck keys for which value needs to be extracted.
 * @returns {Object} filtered object
 */
export function pick(obj, keysToPluck) {
  if (isObject(obj) && isArray(keysToPluck)) {
    return keysToPluck.reduce((acc, key) => {
      acc[key] = obj[key] || null;
      return acc;
    }, {});
  }

  return {};
}

/**
 * Validate whether an input is string or not.
 *
 * @export
 * @param {*} input
 * @returns {boolean} is input is string?
 */
export function isString(input) {
  return typeof input === 'string';
}

/**
 * Validate whether an input is function or not.
 *
 * @export
 * @param {*} input
 * @returns {boolean} is input is function?
 */
export function isFunction(input) {
  return typeof input === 'function';
}

/**
 * Find a array element which satisfies the callback function condition
 *
 * @export
 * @param {Array<*>} inputArray
 * @param {Function} callback
 * @returns {*} array element or undefined
 */
export function find(inputArray, callback) {
  if (isArray(inputArray) && isFunction(callback)) {
    for (let index = 0; index < inputArray.length; index += 1) {
      const currentItem = inputArray[index];

      if (callback(currentItem, index, inputArray)) {
        return currentItem;
      }
    }
  }

  return undefined;
}

export function objectValues(obj) {
  if (isObject(obj)) {
    return Object.keys(obj).map(key => obj[key]);
  }

  return [];
}

export function areObjectValuesShallowNotEqual(firstObj, secondObj, keys) {
  if (!isObject(firstObj) || !isObject(secondObj) || !isArray(keys)) {
    return false;
  }

  return keys.some(key => firstObj[key] !== secondObj[key]);
}

export function getIframeRequestPayload(cache, apiUrl) {
  const { url, query, method, data } = { ...cache.getData(CACHE.API).requestPayload(apiUrl) };
  data.body = { ...data.body, ...query };

  return {
    targetURL: `${url}?${getEncodedQs(query)}`,
    queryParams: query,
    requestData: data,
    method
  };
}

/**
 * @returns {url} for redirection flow(enhanced native)
 */
export function showPaymentPageUrl() {
  return `${API_HOST}/api/v1/showPaymentPage`;
}

const validLinkUrlRegExp = /(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@\-/]))?/;

export function isValidUrl(url) {
  return validLinkUrlRegExp.test(url);
}

export function convertRupeeCharToUnicode(text) {
  const rupeeUnicode = String.fromCodePoint(0x20b9);
  return (text || '').replace(/₹/g, rupeeUnicode);
}

const removeVpaDetails = _data => {
  const data = _data;
  let vpaDetails = getKey(data, 'response.vpaDetails', []);
  let bankAccounts = getKey(data, 'response.bankAccounts', []);
  /* remove all the relevant data related to account number, vpa id */
  vpaDetails = vpaDetails.map(_detail => {
    const detail = _detail;
    detail.defaultCredit = {};
    detail.defaultDebit.accRefNumber = '';
    detail.defaultDebit.account = '';
    detail.defaultDebit.credsAllowed = [];
    detail.defaultDebit.ifsc = '';
    detail.defaultDebit.name = '';
    detail.defaultDebit.accRefId = '';
    detail.name = '';
    return detail;
  });
  bankAccounts = bankAccounts.map(_account => {
    const account = _account;
    account.accRefNumber = '';
    account.account = '';
    account.credsAllowed = [];
    account.ifsc = '';
    account.name = '';
    return account;
  });
  data.response.vpaDetails = vpaDetails;
  data.response.bankAccounts = bankAccounts;
  return data;
};

export function removeDataForLogging(data) {
  data.userDetails = data.userDetails && {
    ...data.userDetails,
    email: '',
    username: '',
    mobile: '',
    paytmCCEnabled: '',
    kyc: ''
  };
  data.merchantDetails = data.merchantDetails && {
    ...data.merchantDetails,
    merchantVpa: ''
  };
  const userProfileSarvatraMerchantPayOption = getKey(
    data,
    'merchantPayOption.userProfileSarvatra',
    null
  );
  const userProfileSarvatraAddMoneyPayOption = getKey(
    data,
    'addMoneyPayOption.userProfileSarvatra',
    null
  );
  if (userProfileSarvatraMerchantPayOption) {
    data.merchantPayOption.userProfileSarvatra = removeVpaDetails(
      userProfileSarvatraMerchantPayOption
    );
  }
  if (userProfileSarvatraAddMoneyPayOption) {
    data.addMoneyPayOption.userProfileSarvatra = removeVpaDetails(
      userProfileSarvatraAddMoneyPayOption
    );
  }
  return data;
}

export function findIndex(array, callbackFn) {
  if (isArray(array) && array.length && isFunction(callbackFn)) {
    for (let index = 0; index < array.length; index += 1) {
      // Explicit check for true
      if (callbackFn(array[index], index) === true) {
        return index;
      }
    }
  }

  return -1;
}

/**
 * Generates UUID
 *
 * @export
 * @returns {string} UUID
 */
export function generateUUID() {
  // Timestamp
  let unixEpochTimestamp = new Date().getTime();
  // Time in microseconds since page-load or 0 if unsupported
  let domHighResTimeStamp = (performance && performance.now() * 1000) || 0;

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
    let random = Math.random() * 16; // random number between 0 and 16
    if (unixEpochTimestamp > 0) {
      // Use timestamp until depleted
      // eslint-disable-next-line no-bitwise
      random = (unixEpochTimestamp + random) % 16 | 0;
      unixEpochTimestamp = Math.floor(unixEpochTimestamp / 16);
    } else {
      // Use microseconds since page-load if supported
      // eslint-disable-next-line no-bitwise
      random = (domHighResTimeStamp + random) % 16 | 0;
      domHighResTimeStamp = Math.floor(domHighResTimeStamp / 16);
    }
    // eslint-disable-next-line no-bitwise
    return (char === 'x' ? random : (random & 0x3) | 0x8).toString(16);
  });
}

/**
 * Createa cloned object
 *
 * @export
 * @param {Object} input
 * @returns {Object} cloned object
 */
export function cloneObject(input) {
  try {
    return JSON.parse(JSON.stringify(input));
  } catch (ex) {
    return input;
  }
}

/**
 * Notify merchant object which returna event based notify methods.
 */
export const notifyMerchant = (function _notifyMerchant() {
  function _invokeNotifyMerchantHandler(event, data, cache) {
    const notifyMerchantHandler = getKey(
      cache.getData(CACHE.MERCHANT_CONFIG),
      'handler.notifyMerchant'
    );

    if (isFunction(notifyMerchantHandler)) {
      notifyMerchantHandler(event, data);
    }
  }

  const _EVENTS = CONSTANT.NOTIFY_MERCHANT_EVENTS;

  return {
    fetchPaymentError: _invokeNotifyMerchantHandler.bind(null, _EVENTS.FETCH_PAYMENT_OPTIONS_ERROR),
    handleError: _invokeNotifyMerchantHandler.bind(null, _EVENTS.HANDLER_ERROR),
    fetchEmiBankListError: _invokeNotifyMerchantHandler.bind(
      null,
      _EVENTS.FETCH_EMI_BANK_LIST_ERROR
    ),
    paymentError: _invokeNotifyMerchantHandler.bind(null, _EVENTS.PAYMENT_ERROR),
    appClosed: _invokeNotifyMerchantHandler.bind(null, _EVENTS.APP_CLOSED)
  };
})();

export const getStaticPath = () => {
  // eslint-disable-next-line no-undef
  let _url = STATIC_HOST;

  try {
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-undef
      if (VERSION) {
        _url += `/${APP_NAME}/${VERSION}`;
      }
    }
  } catch (e) {}
  return _url;
};

export const iframePath = () => {
  return `${getStaticPath()}/assets/iframes/dummy-frame.html`;
};
export function updatePayloadForTenureEMI(cache, payload) {
  const subventionObject = cache.getData(CONSTANT.SUBVENTION_OBJ);
  if (subventionObject) {
    payload.subventionObject = subventionObject;
  }
  return payload;
}

export function updatePayloadForValidateEMI(cache, plan, requestPayload, cacheCardToken) {
  const isEmiSubventionFlow = cache.getData(CONSTANT.IS_EMISUBVENTION_ACCESS_TOKEN_FLOW);
  const subventionObject = cache.getData(CONSTANT.SUBVENTION_OBJ);
  if (isEmiSubventionFlow && subventionObject) {
    const mConfig = cache.getData(CACHE.MERCHANT_CONFIG);
    const emiStrategy = cache.getData(CONSTANT.EMISUBVENTION_STATEGY);
    let itemsFromConfig = getKey(subventionObject, 'items', []);
    const { itemBreakUp } = plan;
    if (emiStrategy === EMI_SUBVENTION_STRATEGY.ITEM_BASED) {
      itemBreakUp.forEach(item => {
        itemsFromConfig = itemsFromConfig.map(i => {
          if (i.id === item.id) {
            i.offerDetails = { offerId: item.offerId };
          }
          return i;
        });
      });
      requestPayload.items = subventionObject.items;
    } else {
      itemBreakUp.forEach(item => {
        if (item.id === CONSTANT.EMI_SUBVENTION) {
          /* For amount based subvention since the items is not available hence if item id
           * is subvention pass that offer id to validate requestpayload  */
          requestPayload.offerDetails = { offerId: item.offerId };
        }
      });
      requestPayload.price = subventionObject.price;
      requestPayload.subventionAmount = subventionObject.subventionAmount;
    }
    requestPayload.customerId = getKey(mConfig, 'emiSubvention.customerId', null);
    const paymentAmount = cache.getData(CONSTANT.BANK_OFFER_TXN_AMOUNT);
    requestPayload.paymentDetails = {
      /* using the initial txn amount being passed from configuration  */
      totalTransactionAmount: paymentAmount || cache.getData(CONSTANT.FALLBACK_TXN_AMOUNT)
    };
    requestPayload.generateTokenForIntent = true;

    if (cacheCardToken) {
      requestPayload.cacheCardToken = cacheCardToken;
    }
  }
  return requestPayload;
}

export function updatePayloadBasedOnStrategy(cache, payload, obj) {
  const { items, price, subventionAmount } = obj;
  const emiStrategy = cache.getData(CONSTANT.EMISUBVENTION_STATEGY);
  if (emiStrategy === EMI_SUBVENTION_STRATEGY.ITEM_BASED) {
    payload.items = items;
  } else {
    payload.price = price;
    payload.subventionAmount = subventionAmount;
    payload.offerDetails = obj.offerDetails;
  }
  return payload;
}

export const shallowEqual = (a, b) => {
  for (const key in a) if (a[key] !== b[key]) return false;
  for (const key in b) if (!(key in a)) return false;
  return true;
};

export const isIphoneDevice = () => {
  const userAgent = window.navigator && window.navigator.userAgent;

  return !!(userAgent.match(/iPad/i) || userAgent.match(/iPhone/i));
};

export function scrollInContentWrap(value, deltaHeight = 0) {
  /* if the value is coming as less than 200 means the header wont be in fix state so hence remove
   * height of big header that is approx 140px, if value is greater than 200px then remove the fixed
   * header height od 60px */
  const element = document.querySelector('#paytm-app .payment-content-wrap');
  const headerHeight = value > 202 ? 60 : 142;
  isMobileDevice() &&
    !isIphoneDevice() &&
    element.scrollTo({
      top: value - headerHeight - deltaHeight, // subtracting 60px for header from offset value
      left: 0,
      behavior: 'smooth'
    });
}

export const deviceHeightLessThan640 = () => {
  return window.innerHeight <= 640;
};

export const scrollWithTimeout = (value, deltaHeight) => {
  const timeoutValue = deviceHeightLessThan640() ? 500 : 300;
  setTimeout(() => {
    scrollInContentWrap(value, deltaHeight);
  }, timeoutValue);
};
/* a function to return whether cvv is required for given saved card */
export const savedCardWithCVV = option => {
  /* setting cvvRequired as true if not coming in saved Card object */
  return getKey(option, 'cardDetails.cvvRequired', true);
};

export const scrollToSelectedOption = option => {
  let defaultSelectedPaymodeOffSetTop = getKey(
    option,
    'reference._component.componentRef.offsetTop',
    0
  );
  /* adding 20px to scroll whem mgv since it is on top and scrolling to mgv the header hides the
  selected paymode */

  if (option.type === 'mgv') {
    defaultSelectedPaymodeOffSetTop += 20;
  }
  /* if pamode type is sc, card or ppbl dont scroll down yet because of iframe load issues */
  const optionType = [CONSTANT.SC, PAYMODE.CARD, CONSTANT.PPBL];
  defaultSelectedPaymodeOffSetTop &&
    !optionType.some(type => equalsIgnoreCase(getKey(option, 'type'), type)) &&
    isMobileDevice() &&
    !isIphoneDevice() &&
    scrollInContentWrap(defaultSelectedPaymodeOffSetTop);

  /* if the option is a saved card and has cvv required then only open keyboard,
   * saved card like maestro dont have cvv required but keyboard was opening on the same */
  if (
    !isIphoneDevice() &&
    savedCardWithCVV(option) &&
    optionType.some(type => equalsIgnoreCase(getKey(option, 'type'), type))
  ) {
    setTimeout(() => {
      const hiddenInput = document.getElementById('ptm-hidden-input');
      hiddenInput && hiddenInput.focus();
    }, 50);
  }
};

export const getZeroSubscriptionText = cache => {
  let infotext = '';
  const txnAmount = cache.getData(CONSTANT.TXN_AMOUNT);
  if (txnAmount === '0') {
    infotext = `${
      clientMessage(CONSTANT.SUBSCRIPTION.toLowerCase(), cache).label.upto
    } ${String.fromCodePoint(0x20b9)}${commas(CONSTANT.SUBS_PAYMODE_MIN_AMOUNT)} ${
      clientMessage(CONSTANT.SUBSCRIPTION.toLowerCase(), cache).label.maybe
    }`;
  }
  return infotext;
};

export const isUrlAbsolute = url => url.indexOf('http://') === 0 || url.indexOf('https://') === 0;



// WEBPACK FOOTER //
// ./utils/common.js